import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useFormily } from "../../../../hooks/useFormily";
import { Field, FormConsumer, FormProvider } from "@formily/react";
import { OffsetInput } from "../../../../components/fields/formily/NumberInput";
import { Modal } from "../../../../components/core/basic/RACModal";
import { Button } from "../../../../components/core/basic/Button";
import { VStack } from "../../../../../styled-system/jsx";
import { css } from "../../../../../styled-system/css";
import { useUpdateProductOffsets } from "../../api/useUpdateProductOffsets";
import { OffsetDisplay } from "../../../../components/fields/base/NumberInput";
import { type Offset } from "zodios/apiDefinitions/pricing";

interface Props {
  planId: number;
  productData: { id: number; sku: string; offset_amount: number; offset_type: Offset["type"] }[];
}

export const SetProductPriceOffsetModal = NiceModal.create(({ planId, productData }: Props) => {
  const modal = useModal();
  const updateOffsetMutation = useUpdateProductOffsets();

  const { amounts, types } = productData.reduce(
    (acc, product) => {
      acc.amounts.add(product.offset_amount);
      acc.types.add(product.offset_type);
      return acc;
    },
    {
      amounts: new Set<number>(),
      types: new Set<Offset["type"]>(),
    },
  );

  const sameInitialOffsets = amounts.size === 1 && types.size === 1;

  const initialOffset: Offset =
    sameInitialOffsets ?
      { amount: amounts.values().next().value!, type: types.values().next().value! }
    : {
        amount: 0,
        type: "percent",
      };

  const form = useFormily({ initialValues: { offset: initialOffset } });

  return (
    <Modal
      isOpen={modal.visible}
      isDismissable
      onOpenChange={(isOpen) => (isOpen ? void 0 : modal.remove())}
      title={"Set Offset"}
      className={css({ width: "fit-content", height: "fit-content" })}
    >
      <FormProvider form={form}>
        <FormConsumer>
          {() => (
            <VStack alignItems={"center"} paddingX="medium">
              {!sameInitialOffsets && (
                <figure
                  className={css({
                    fontSize: "medium",
                  })}
                >
                  <figcaption>Current offsets differ between selected products</figcaption>
                  <ul>
                    {productData.map((product) => (
                      <li key={product.id}>
                        {`${product.sku}: `}
                        <OffsetDisplay
                          value={{
                            amount: product.offset_amount,
                            type: product.offset_type,
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </figure>
              )}
              <Field
                name={"offset"}
                title={"Offset price by"}
                component={[OffsetInput, { className: css({ maxWidth: "300px" }) }]}
                description={"Setting an offset can make the price go outside of min/max boundaries"}
              />
              <Button
                isPending={form.submitting}
                onPress={async () =>
                  await form.submit(async (values) => {
                    await updateOffsetMutation.mutateAsync({
                      plan_id: planId,
                      offset: values.offset,
                      product_ids: productData.map((product) => product.id),
                    });
                    modal.remove();
                  })
                }
              >
                Save
              </Button>
            </VStack>
          )}
        </FormConsumer>
      </FormProvider>
    </Modal>
  );
});
